import React, { useEffect, useState, useMemo, useRef, Suspense } from 'react'
import { useLazyGetHourAnalysisQuery, useLazyGetSeuHourAnalysisQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice'
import { useDispatch } from 'react-redux'
import { showSnackbar } from '../../../../redux/snackbarSlice'
import { Box, Grid, Card, CardContent, Autocomplete, TextField, InputAdornment, Avatar, List, ListSubheader, ListItemAvatar, ListItem, ListItemText, Typography, IconButton, Button, Stack, ListItemButton, ListItemIcon, Alert, AlertTitle } from '@mui/material';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HtmlTooltip from '../../../../components/tooltip/HtmlTooltip';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

const EnergyAnalyzerFilterInput = React.lazy(() => import('remote/EnergyAnalyzerFilterInput').catch(() => ({ default: () => <div>Failed to load</div> })));

import { DateTime } from 'luxon';

const HourAnalysisChart = React.lazy(() => import('remote/HourAnalysisChart').catch(() => ({ default: () => <div>Failed to load</div> })));

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const HourAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  timeRange
}) => {

  const [getHourAnalysis, { data: hourAnalysisData, isLoading: isHourAnalysisLoading, isFetching: isHourAnalysisFetching }] = useLazyGetHourAnalysisQuery();
  const [getSeuHourAnalysis, { data: seuHourAnalysisData, isLoading: isSeuHourAnalysisLoading, isFetching: isSeuHourAnalysisFetching }] = useLazyGetSeuHourAnalysisQuery();

  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedRange([
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ]);
  }, [timeRange]);

  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  const [nodeToAnalyse, setNodeToAnalyse] = useState(null)
  const [seuToAnalyse, setSeuToAnalyse] = useState(null)
  const [analysisMode, setAnalysisMode] = useState('node'); // or 'seu'
  const [data, setData] = useState([])
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [viewMode, setViewMode] = useState('column');

  const [hourAnalysisFilters, setHourAnalysisFilters] = useState({});

  const handleApplyFilters = async (filters) => {
    setHourAnalysisFilters(filters);
  }

  const fetchHourAnalysis = async ({
    start,
    end,
    filters
  }) => {
    try {
      const response = await getHourAnalysis({
        accountId: selectedAccount.AccountId,
        energyGroupId: selectedEnergyGroup.energyGroupId,
        nodeId: nodeToAnalyse?.energyTreeNodeId,
        start,
        end,
        ...filters
      }).unwrap();
      return response;
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error.data.message,
          severity: 'error',
        })
      );
      setData(null)
      return null;
    }
  }

  const fetchSeuHourAnalysis = async ({
    start,
    end,
    filters
  }) => {
    try {
      const response = await getSeuHourAnalysis({
        accountId: selectedAccount.AccountId,
        energyGroupId: selectedEnergyGroup.energyGroupId,
        seu: seuToAnalyse,
        start,
        end,
        ...filters
      }).unwrap();
      return response;
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error.data.message,
          severity: 'error',
        })
      );
      setData(null)
      return null;
    }
  }

  useEffect(() => {
    const start = selectedRange[0].toMillis();
    const end = selectedRange[1].toMillis();

    if (analysisMode === 'node' && nodeToAnalyse) {
      fetchHourAnalysis({ start, end, filters: hourAnalysisFilters }).then((response) => {
        if (response) setData(response);
      });
    }

    if (analysisMode === 'seu' && seuToAnalyse) {
      fetchSeuHourAnalysis({ start, end, filters: hourAnalysisFilters }).then((response) => {
        if (response) setData(response);
      });
    }
  }, [analysisMode, nodeToAnalyse, seuToAnalyse, selectedRange, hourAnalysisFilters]);

  useEffect(() => {
    if (!nodeToAnalyse && selectedEnergyGroup?.energyTreeNodes.length > 0) {
      setNodeToAnalyse(selectedEnergyGroup?.energyTreeNodes[0])
    }
  }, [selectedEnergyGroup])

  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach(node => {
      if (!map.has(node.energyTreeNodeId)) {
        map.set(node.energyTreeNodeId, { ...node, children: [] });
      }
      if (node.parentId) {
        if (!map.has(node.parentId)) {
          map.set(node.parentId, { children: [] });
        }
        map.get(node.parentId).children.push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach(node => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(() => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [], [nodeToAnalyse, nodeLookup]);
  const parent = useMemo(() => nodeLookup.get(nodeToAnalyse?.parentId) || null, [nodeToAnalyse, nodeLookup]);

  const seuInputRef = useRef(null);

  const validSeuOptions = useMemo(() => {
    return Array.from(nodeGroupsByType.entries())
      .filter(([type, nodes]) =>
        nodes.some(node => node?.dataStreams?.length > 0)
      )
      .map(([type]) => type)
      .filter(type => type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle');
  }, [nodeGroupsByType]);

  const effectiveSeuToAnalyse = seuToAnalyse ?? validSeuOptions[0];

  useEffect(() => {
    if (analysisMode === 'seu' && seuInputRef.current) {
      // blur the input immediately after mounting so dropdown doesn't pop open
      seuInputRef.current.blur();
      setSeuToAnalyse(effectiveSeuToAnalyse)
    }
  }, [analysisMode]);


  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={filtersOpen ? 8 : 12} xl={filtersOpen ? 9 : 12} sx={{ transition: 'flex-basis 0.3s ease' }}>
          <Card variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <CardContent sx={{ minHeight: 600 }}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <>
                        <ListItemText
                          primary={analysisMode === 'node'
                            ? `Node`
                            : `SEU`}
                          primaryTypographyProps={{ color: 'text.secondary', variant: 'body2', }}
                          secondary={analysisMode === 'node'
                            ? `${nodeToAnalyse?.name}`
                            : `${seuToAnalyse}`}
                          secondaryTypographyProps={{ color: 'text.primary', variant: 'h6', fontWeight: 'bold' }}
                        />
                        <HtmlTooltip title="Døgnforbrugsanalysen viser, hvordan energiforbruget på et målepunkt fordeler sig hen over døgnets 24 timer i den valgte periode. Ved valg af boksplot vises variationen i forbruget for hver time – median, minimum, maksimum og spredning – hvilket giver et hurtigt overblik over stabilitet og udsving i forbruget." placement="right">
                          <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                        </HtmlTooltip>
                      </>
                    </Box>

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      I perioden {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : 'Vælg periode'}
                    </Typography>
                  </Box>

                  <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">

                    <HtmlTooltip title="Gennemsnitsvisning i kolonner." placement="top">
                      <IconButton onClick={() => setViewMode('column')} sx={{ borderRadius: 3, border: viewMode === 'column' ? '1px solid' : 'none', borderColor: 'divider', mr: 1 }}>
                        <BarChartRoundedIcon />
                      </IconButton>
                    </HtmlTooltip>

                    <HtmlTooltip title="Vis som boksplot for at se variationen i timeforbruget." placement="top">
                      <IconButton onClick={() => setViewMode('box')} sx={{ p: 1, borderRadius: 3, border: viewMode === 'box' ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <TuneRoundedIcon sx={{ transform: 'rotate(90deg)' }} />
                      </IconButton>
                    </HtmlTooltip>

                    <HtmlTooltip title={`${filtersOpen ? 'Skjul' : 'Vis'} filtre`} placement="top">
                      <IconButton
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, ml: 4 }}
                      >
                        {filtersOpen ? <KeyboardArrowRightRoundedIcon /> : <KeyboardArrowLeftRoundedIcon />}
                      </IconButton>
                    </HtmlTooltip>

                  </Box>
                </Box>
              </Stack>
              {data ? 
                <Suspense fallback={<div>Loading...</div>}>
                  <HourAnalysisChart
                    data={data}
                    viewMode={viewMode}
                    isLoading={isHourAnalysisFetching || isHourAnalysisLoading}
                  />
                </Suspense>
                :
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>Ingen data tilgængelig</AlertTitle>
                  Det er ikke muligt at vise døgnforbrugsanalyse.
                </Alert>
              }
            </CardContent>
          </Card>
        </Grid>


        {filtersOpen &&
          <Grid item xs={12} md={3}>
            <Grid item xs>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1" color="textSecondary" fontWeight="bold">
                      Filtrering
                    </Typography>
                    <HtmlTooltip title="Anvend filtrering på data for et bedre indblik." placement="right">
                      <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                    </HtmlTooltip>
                  </Box>
                  <Suspense fallback={<div>Loading...</div>}>
                    <EnergyAnalyzerFilterInput
                      handleApply={handleApplyFilters}
                      setComparisonFilters={setHourAnalysisFilters}
                      context="hourAnalysis"
                    />
                  </Suspense>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs sx={{ mt: 2 }}>

              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
                    <Grid item xs>
                      {analysisMode === 'node' ? (
                        <>
                          <Autocomplete
                            options={selectedEnergyGroup?.energyTreeNodes || []}
                            getOptionLabel={(option) => option?.name || ""}
                            value={nodeToAnalyse || '-'}
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setNodeToAnalyse(newValue);
                                setSeuToAnalyse(null);
                                setAnalysisMode('node');
                              }
                            }}
                            disableClearable
                            renderOption={(props, option) => (

                              <ListItem
                                {...props}
                                key={option.id}
                                sx={{
                                  '&:hover .choose-type-btn': {
                                    visibility: 'visible'
                                  },
                                  justifyContent: 'space-between',
                                }}
                                disableGutters
                                secondaryAction={validSeuOptions.includes(option.type) && (
                                  <HtmlTooltip
                                    placement="top"
                                    title={
                                      <Box display="flex" alignItems="center">
                                        <ListItemAvatar>
                                          <Avatar
                                            variant="rounded"
                                            sx={{
                                              backgroundColor: getTypeColor(option.type) || "#555",
                                              color: "#fff",
                                              fontWeight: "bold",
                                              borderRadius: 2
                                            }}
                                          >
                                            <i className={`fas ${getTypeIcon(option.type)}`}></i>
                                          </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                          primary={option.type}
                                          secondary={
                                            <>
                                              Der er fundet{' '}
                                              <strong>
                                                {nodeGroupsByType.get(option.type)?.length}{' '}
                                                {nodeGroupsByType.get(option.type)?.length === 1
                                                  ? 'målepunkt'
                                                  : 'målepunkter'}
                                              </strong>{' '}
                                              af denne type.
                                            </>
                                          }
                                        />
                                      </Box>
                                    }
                                  >
                                    <Box
                                      className="choose-type-btn"
                                      sx={{
                                        visibility: 'hidden',
                                        mr: 2,
                                        flexShrink: 0
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: 'text.secondary', pr: 1 }}
                                      >
                                        {option?.type}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="primary"
                                        sx={{
                                          cursor: 'pointer',
                                          '&:hover': { textDecoration: 'underline' }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSeuToAnalyse(option.type);
                                          setAnalysisMode('seu');
                                        }}
                                      >
                                        Vælg SEU type
                                      </Typography>
                                    </Box>
                                  </HtmlTooltip>
                                )}
                              >
                                <Box display="flex" alignItems="center">
                                  <ListItemAvatar>
                                    <Avatar
                                      variant="rounded"
                                      sx={{
                                        backgroundColor: getTypeColor(option.type) || "#555",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        borderRadius: 2
                                      }}
                                    >
                                      <i className={`fas ${getTypeIcon(option.type)}`}></i>
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText primary={option.name} />
                                </Box>
                              </ListItem>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Vælg målepunkt"
                                variant="outlined"
                                fullWidth
                                sx={{
                                  "& .MuiInputBase-root": { borderRadius: "10px" },
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                                  },
                                  "& .MuiInputLabel-root": { color: "gray" },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: nodeToAnalyse ? (
                                    <InputAdornment position="start">
                                      <Avatar variant="rounded" sx={{
                                        backgroundColor: getTypeColor(nodeToAnalyse.type) || "#555",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        borderRadius: 2
                                      }}><i className={`fas ${getTypeIcon(nodeToAnalyse.type)}`}></i></Avatar>
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}
                          />
                          <Box mt={1} display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('seu');
                              }}
                              sx={{ textTransform: 'none' }}
                              size="small"
                            >
                              Vælg SEU typer
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Autocomplete
                            options={validSeuOptions}
                            getOptionLabel={(option) => option}
                            value={seuToAnalyse}
                            onChange={(e, newValue) => {
                              setSeuToAnalyse(newValue);
                            }}
                            disableClearable
                            renderOption={(props, option) => {
                              const isDisabled = !validSeuOptions.includes(option);
                              return (
                                <li
                                  {...props}
                                  key={option}
                                  style={{
                                    ...props.style,
                                    opacity: isDisabled ? 0.4 : 1,
                                    pointerEvents: isDisabled ? 'none' : 'auto',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {option}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Vælg SEU type"
                                inputRef={seuInputRef}
                                autoFocus={false}
                                variant="outlined"
                                sx={{ "& .MuiInputBase-root": { borderRadius: "10px" } }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: seuToAnalyse ? (
                                    <InputAdornment position="start">
                                      <Avatar variant="rounded" sx={{
                                        backgroundColor: getTypeColor(seuToAnalyse) || "#555",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        borderRadius: 2
                                      }}>
                                        <i className={`fas ${getTypeIcon(seuToAnalyse)}`}></i>
                                      </Avatar>
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}
                          />

                          <Box mt={1} display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('node');
                                // setSeuToAnalyse(null);
                              }}
                              sx={{ textTransform: 'none' }}
                              size="small"
                            >
                              Vælg målepunkter
                            </Button>
                          </Box>
                        </>
                      )}
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => parent && setNodeToAnalyse(parent)}
                        disabled={!parent || analysisMode === 'seu'}
                        sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
                      >
                        <ArrowUpwardRoundedIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  {analysisMode === 'node' && (
                    <List
                      dense
                      sx={{
                        maxHeight: 425, // or whatever height fits your layout
                        overflowY: 'auto',
                      }}
                    >
                      {children.length > 0 ? (
                        children.map(child => (
                          <ListItemButton key={child.id} onClick={() => setNodeToAnalyse(child)} sx={{ borderRadius: 2 }}>
                            <ListItemIcon>
                              <Avatar
                                variant="rounded"
                                size="small"
                                sx={{
                                  backgroundColor: getTypeColor(child.type) || "#555",
                                  color: "#fff",
                                  fontWeight: "bold",
                                  borderRadius: 2
                                }}
                              >
                                <i className={`fas ${getTypeIcon(child.type)}`}></i>
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={child.name}
                              secondary={child.type}
                            />
                          </ListItemButton>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'gray' }}>
                          Ingen yderligere neddeling
                        </Typography>
                      )}
                    </List>
                    )}
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        }
      </Grid>
    </Box>
  )
}

export default HourAnalysisTab